import {
  Button,
  Checkbox,
  Grid,
  Icon,
  Input,
  InputOnChangeData,
  Modal
} from 'semantic-ui-react';
import {
  ChannelToggleContent,
  CoverageRow,
  CoverageTitleRow,
  FireInsurersRow,
  InsurerCard,
  InsurerCardContent,
  InsurerCardContentTitle
} from '../../../styles';
import { useEffect, useState } from 'react';
import { get } from '../../../../../services/request';
import React from 'react';
import Toggle from '../../../../../components/Toggle';
import { Field } from 'formik';
import { formatAmount } from '../../../../../services/masks';
import { YelumFireInsuranceConfigProps } from '..';
import Loader from '../../../../../components/Loader';

export enum ModalitiesEnum {
  Residencial = 'Residencial',
  Comercial = 'Comercial'
}

interface ChannelInterface {
  id: string;
  name: string;
  code: string;
  modality: ModalitiesEnum;
  selected: boolean;
  active: boolean;
}

interface CoverageInterface {
  id: string;
  insurerId: string;
  channelId: string;
  name: string;
  code: string;
  maxValue: string;
  minValue: string;
  selectedMinValue: string;
  selectedMaxValue: string;
  multiplier: number;
  maxLmi: number;
  minLmi: number;
  selected: boolean;
  value: string;
  isDefault: boolean;
  isRequired: boolean;
  maxMultiplier: number;
  propertyType?: string;
  label?: string;
  // Campos de configuração de default
  defaultValue?: number;
  defaultMultiplier?: number;
  defaultEnabled?: boolean;
  selectedDefaultValue?: string;
}

interface CategoryInterface {
  id: string;
  name: string;
  type: 'coverages' | 'assistances';
  active: boolean;
}

// Tipos de imóveis suportados pela Yelum
const PROPERTY_TYPES = [
  'Apartamento',
  'Residência Alvenaria',
  'Casa Madeira',
  'Escritório e Consultório',
  'Comércio e Serviço'
];

/**
 * Funções para obter valores padrão das coberturas
 * Prioridade: valores do backend > fallback
 */

// Obtém o multiplicador padrão da cobertura
const getDefaultMultiplier = (coverage: any): number => {
  // Prioridade: defaultMultiplier do backend > fallback 20
  if (coverage?.defaultMultiplier && coverage.defaultMultiplier > 0) {
    return coverage.defaultMultiplier;
  }
  return 20;
};

// Obtém o valor padrão fixo da cobertura (em reais)
const getDefaultValue = (coverage: any): number | undefined => {
  if (coverage?.defaultValue && coverage.defaultValue > 0) {
    return coverage.defaultValue;
  }
  return undefined;
};

// Obtém se a cobertura deve vir habilitada por padrão
const getDefaultEnabled = (coverage: any): boolean => {
  // Usar APENAS defaultEnabled do backend
  // Se não estiver definido, retorna false (não assinalado)
  return coverage?.defaultEnabled === true;
};

// Função para buscar limite usando LIKE nas primeiras palavras
const getLimitByCoverageLike = (
  coverageLabel: string,
  propertyLimits: Record<string, number>
): number | undefined => {
  // Se começar com INCENDIO, usar apenas a primeira palavra
  if (coverageLabel.startsWith('INCENDIO')) {
    const searchPattern = 'INCENDIO';
    const matchingKey = Object.keys(propertyLimits).find(key =>
      key.startsWith(searchPattern)
    );
    return matchingKey ? propertyLimits[matchingKey] : undefined;
  }

  // Para os demais, usar as 2 primeiras palavras
  const words = coverageLabel.split('_');
  if (words.length >= 2) {
    const searchPattern = `${words[0]}_${words[1]}`;
    const matchingKey = Object.keys(propertyLimits).find(key =>
      key.startsWith(searchPattern)
    );
    return matchingKey ? propertyLimits[matchingKey] : undefined;
  }

  // Fallback: busca exata
  return propertyLimits[coverageLabel];
};

// Limites padrão por tipo de imóvel (do backend)
const YELUM_PROPERTY_LIMITS: Record<string, Record<string, number>> = {
  Apartamento: {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 125000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
    //IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000,
    // DANOS_CAUSADOS_AOS_BENS_DO_CONDOMINIO_VERBA_UNICA: 10000
  },
  'Residência Alvenaria': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 125000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 150000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 250000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 500000,
    IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 500000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 100000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000,
    // DANOS_CAUSADOS_AOS_BENS_DO_CONDOMINIO_VERBA_UNICA: 10000
  },
  'Casa Madeira': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 375000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 375000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 375000,
    DANOS_ELETRICOS_VERBA_UNICA: 30000,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 30000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 30000,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 30000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 30000,
    IMPACTO_DE_VEICULOS_TERRESTRES_VERBA_UNICA: 30000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 20000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000.01
  },
  'Escritório e Consultório': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 2500000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 2500000,
    DANOS_ELETRICOS_VERBA_UNICA: 62500,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 200000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 50000
  },
  'Comércio e Serviço': {
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO_E_CONTEUDO: 1000000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_CONTEUDO: 207000,
    INCENDIO_EXPLOSAO_DE_QUALQUER_NATUREZA_FUMACA_E_QUEDA_DE_AERONAVES_PREDIO: 1000000,
    DANOS_ELETRICOS_VERBA_UNICA: 62500,
    ROUBO_E_OU_SUBTRACAO_DE_BENS_COM_VESTIGIOS_VERBA_UNICA: 50000,
    VENDAVAL_FURACAO_CICLONE_TORNADO_E_GRANIZO_VERBA_UNICA: 93750,
    PERDA_OU_PAGAMENTO_DE_ALUGUEL_VERBA_UNICA: 80000,
    RESPONSABILIDADE_CIVIL_FAMILIAR_VERBA_UNICA: 100000,
    QUEBRA_DE_VIDROS_ESPELHOS_MARMORES_E_GRANITOS_VERBA_UNICA: 150000,
    ROMPIMENTO_DE_TUBULACOES_DE_AGUA_VERBA_UNICA: 10000
  }
};

const CoveragesConfigModal = ({ values, ...props }: YelumFireInsuranceConfigProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [coveragesByPropertyType, setCoveragesByPropertyType] = useState<{
    [key: string]: CoverageInterface[];
  }>({});
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>('Apartamento');
  const [assistances, setAssistances] = useState<CoverageInterface[]>([]);
  const [categories, setCategories] = useState<CategoryInterface[]>([
    { id: '1', name: 'Coberturas', type: 'coverages', active: true },
    { id: '2', name: 'Assistências', type: 'assistances', active: false }
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!open) return;

    const fetchCoverages = async () => {
      setIsLoading(true);

      const response = await get(
        '/alfa-seguros/coverage?insurerId=9ebc4304-201d-4cf2-9c8a-6d8cc90ec968'
      );

      if (response.length > 0) {
        const savedCoverages =
          values.insurerSpecificData?.yelum?.fireInsurance?.coverages || [];

        // Criar configurações separadas por tipo de imóvel
        const coveragesByType: { [key: string]: CoverageInterface[] } = {};

        PROPERTY_TYPES.forEach(propertyType => {
          const propertyLimits = YELUM_PROPERTY_LIMITS[propertyType] || {};

          const coveragesForType = response
            .filter((c: any) => !c.label?.includes('ASSISTENCIA'))
            .filter((coverage: any) => {
              // Filtrar coberturas por código na label
              const label = coverage.label || '';

              // Se contém código 1112: apenas Escritório e Consultório e Comércio e Serviço
              if (label.includes('1112')) {
                return (
                  propertyType === 'Escritório e Consultório' ||
                  propertyType === 'Comércio e Serviço'
                );
              }

              // Se contém código 11054: apenas Apartamento, Residência Alvenaria e Casa Madeira
              if (label.includes('11054')) {
                return (
                  propertyType === 'Apartamento' ||
                  propertyType === 'Residência Alvenaria' ||
                  propertyType === 'Casa Madeira'
                );
              }

              // Para outras coberturas, incluir em todos os tipos de imóvel
              return true;
            })
            .map((coverage: CoverageInterface) => {
              const isDefault = coverage.isDefault;
              const label = (coverage as any).label;
              const limitePadrao = getLimitByCoverageLike(label, propertyLimits);

              // Buscar configuração salva para este tipo de imóvel e cobertura
              // Se for a primeira vez (dados legados), busca coberturas com propertyType null
              let savedCoverage = savedCoverages.find(
                (sc: any) => sc.id === coverage.id && sc.propertyType === propertyType
              );

              if (!savedCoverage && propertyType === 'Apartamento') {
                savedCoverage = savedCoverages.find(
                  (sc: any) =>
                    sc.id === coverage.id &&
                    (sc.propertyType === null || !sc.propertyType)
                );
              }

              let coverageData;
              if (savedCoverage) {
                coverageData = {
                  ...coverage,
                  selected: savedCoverage.enabled,
                  isRequired: false, // Coberturas Yelum não são obrigatórias
                  selectedMinValue: formatAmount(
                    Number(savedCoverage.minValue).toFixed(2) || ''
                  ),
                  selectedMaxValue: formatAmount(
                    Number(savedCoverage.maxValue).toFixed(2) || ''
                  ),
                  multiplier: savedCoverage.multiplier,
                  propertyType: propertyType,
                  label,
                  // Campos de default
                  defaultValue: savedCoverage.defaultValue || 0,
                  defaultEnabled: savedCoverage.defaultEnabled ?? false,
                  selectedDefaultValue: savedCoverage.defaultValue
                    ? formatAmount(Number(savedCoverage.defaultValue).toFixed(2))
                    : ''
                };
              } else {
                // Usar valores padrão do backend - APLICAR SEMPRE POR PADRÃO
                const valorPadraoFixo = getDefaultValue(coverage);
                const multiplicadorPadrao = getDefaultMultiplier(coverage);
                const habilitadoPadrao = getDefaultEnabled(coverage);

                // Valor máximo: usar limitePadrao ou 100.000 como fallback
                const valorMaximoPadrao = limitePadrao || 100000;

                // Lógica exclusiva: se tem valor padrão, não tem multiplicador e vice-versa
                const temValorPadrao = valorPadraoFixo && valorPadraoFixo > 0;
                const temMultiplicador = coverage?.defaultMultiplier && coverage.defaultMultiplier > 0;

                coverageData = {
                  ...coverage,
                  selected: habilitadoPadrao,
                  isRequired: false, // Coberturas Yelum não são obrigatórias
                  selectedMinValue: formatAmount(
                    Number(coverage.minValue || 0).toFixed(2)
                  ),
                  selectedMaxValue: formatAmount(valorMaximoPadrao.toFixed(2)),
                  // Se tem valor padrão fixo, multiplicador fica vazio; se tem multiplicador, valor padrão fica vazio
                  multiplier: temValorPadrao ? 0 : (temMultiplicador ? multiplicadorPadrao : 0),
                  propertyType: propertyType,
                  label,
                  // Campos de default
                  defaultValue: temValorPadrao ? valorPadraoFixo : 0,
                  defaultEnabled: habilitadoPadrao,
                  selectedDefaultValue: temValorPadrao
                    ? formatAmount(Number(valorPadraoFixo).toFixed(2))
                    : ''
                };
              }

              // Renomear coberturas de incêndio
              if (label?.includes('INCENDIO')) {
                if (label.includes('PREDIO_E_CONTEUDO')) {
                  coverageData.name =
                    coverageData.name.slice(0, 30) + '... (PRÉDIO E CONTEÚDO)';
                } else if (label.includes('PREDIO')) {
                  coverageData.name = coverageData.name.slice(0, 30) + '... (PRÉDIO)';
                } else if (label.includes('CONTEUDO')) {
                  coverageData.name = coverageData.name.slice(0, 30) + '... (CONTEÚDO)';
                }
              }

              return coverageData;
            })
            .sort((a: any, b: any) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );

          coveragesByType[propertyType] = coveragesForType;
        });

        // Processar assistências (não têm tipo de imóvel)
        const assistances = response
          .filter((c: any) => c.label?.includes('ASSISTENCIA'))
          .map((coverage: CoverageInterface) => {
            const savedAssistance = savedCoverages.find(
              (sc: any) => sc.id === coverage.id && !sc.propertyType
            );

            return savedAssistance
              ? {
                ...coverage,
                selected: savedAssistance.enabled
              }
              : {
                ...coverage,
                selected: coverage.isDefault
              };
          })
          .sort((a: any, b: any) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          );

        setCoveragesByPropertyType(coveragesByType);
        setAssistances(assistances);
      }

      setIsLoading(false);
    };

    fetchCoverages();
  }, [open]);

  return (
    <Modal
      style={{ width: '90%' }}
      open={open}
      onClose={() => setOpen(false)}
      trigger={
        <Button
          color="blue"
          fluid
          type="button"
          size={'large'}
          onClick={() => setOpen(true)}
        >
          Gerenciar Coberturas
        </Button>
      }
    >
      <Modal.Header>
        <h3>
          <Icon name="shield" />
          Configurar coberturas
        </h3>
      </Modal.Header>

      <Modal.Content scrolling>
        <Loader active={isLoading} />
        <FireInsurersRow>
          <Grid.Column mobile={16} tablet={8} computer={8}>
            <h4 style={{ marginBottom: '10px' }}>CATEGORIAS</h4>
            {categories.map(category => {
              return (
                <InsurerCard
                  key={category.id}
                  isSelected={category.active}
                  onClick={() =>
                    setCategories(
                      categories.map(c => {
                        if (c.id === category.id) {
                          c.active = true;
                        } else {
                          c.active = false;
                        }
                        return c;
                      })
                    )
                  }
                >
                  <InsurerCardContent>
                    <InsurerCardContentTitle>{category.name}</InsurerCardContentTitle>
                  </InsurerCardContent>
                </InsurerCard>
              );
            })}
          </Grid.Column>

          {categories.find(c => c.active)?.type === 'coverages' &&
            Object.keys(coveragesByPropertyType).length > 0 && (
              <Grid.Column
                style={{ marginLeft: '40px' }}
                mobile={16}
                tablet={8}
                computer={8}
              >
                {/* Seletor de tipo de imóvel */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ marginBottom: '10px' }}>TIPO DE IMÓVEL</h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {PROPERTY_TYPES.map(type => (
                      <Button
                        key={type}
                        type="button"
                        color={selectedPropertyType === type ? 'blue' : undefined}
                        onClick={() => setSelectedPropertyType(type)}
                      >
                        {type}
                      </Button>
                    ))}
                  </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ textAlign: 'left', padding: '10px', minWidth: '280px' }}>TIPO</th>
                      <th style={{ textAlign: 'center', padding: '10px', width: '110px' }}>VALOR MÍN</th>
                      <th style={{ textAlign: 'center', padding: '10px', width: '110px' }}>VALOR MÁX</th>
                      <th style={{ textAlign: 'center', padding: '10px', width: '80px' }}>MULT.</th>
                      <th style={{ textAlign: 'center', padding: '10px', width: '110px' }}>VALOR PADRÃO</th>
                      <th style={{ textAlign: 'center', padding: '10px', width: '80px' }}>PADRÃO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(coveragesByPropertyType[selectedPropertyType] || []).map(coverage => {
                      const isFireCoverage = coverage.name.includes('Incêndio');

                      return (
                        <tr
                          key={`${coverage.id}-${selectedPropertyType}`}
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: coverage.selected ? '#f8f9fa' : 'transparent'
                          }}
                        >
                          <td style={{ padding: '8px 10px' }}>
                            <Toggle
                              name={coverage.id}
                              id={`coverage-${coverage.id}-${selectedPropertyType}`}
                              checked={coverage?.selected || false}
                              disabled={isFireCoverage}
                              onChange={() =>
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.selected = !c.selected;
                                      }
                                      return c;
                                    }
                                  )
                                }))
                              }
                              label={
                                <label style={{ fontSize: '13px' }}>
                                  {coverage.name}
                                </label>
                              }
                            />
                          </td>

                          <td style={{ padding: '8px 5px' }}>
                            <Field
                              style={{ width: '100%' }}
                              name={`coverages.${coverage.id}.minValue`}
                              value={coverage?.selectedMinValue || ''}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                                data: InputOnChangeData
                              ) => {
                                data.value = formatAmount(data.value);
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.selectedMinValue = data.value;
                                      }
                                      return c;
                                    }
                                  )
                                }));
                              }}
                              component={Input}
                              fluid
                              size="small"
                            />
                          </td>

                          <td style={{ padding: '8px 5px' }}>
                            <Field
                              style={{ width: '100%' }}
                              name={`coverages.${coverage.id}.maxValue`}
                              value={coverage?.selectedMaxValue || ''}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                                data: InputOnChangeData
                              ) => {
                                data.value = formatAmount(data.value);
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.selectedMaxValue = data.value;
                                      }
                                      return c;
                                    }
                                  )
                                }));
                              }}
                              component={Input}
                              fluid
                              size="small"
                            />
                          </td>

                          <td style={{ padding: '8px 5px' }}>
                            <Field
                              style={{ width: '100%' }}
                              name={`coverages.${coverage.id}.multiplier`}
                              value={coverage?.multiplier || ''}
                              type="number"
                              min="1"
                              max={coverage?.name.includes('Aluguel') ? 6 : null}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                                data: InputOnChangeData
                              ) => {
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.multiplier = Number(data.value);
                                        // Se tem multiplicador, limpa valor padrão
                                        if (data.value && Number(data.value) > 0) {
                                          c.defaultValue = 0;
                                          c.selectedDefaultValue = '';
                                        }
                                      }
                                      return c;
                                    }
                                  )
                                }));
                              }}
                              component={Input}
                              fluid
                              size="small"
                            />
                          </td>

                          <td style={{ padding: '8px 5px' }}>
                            <Field
                              style={{ width: '100%' }}
                              name={`coverages.${coverage.id}.defaultValue`}
                              value={coverage?.selectedDefaultValue || ''}
                              placeholder="R$ 0,00"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                                data: InputOnChangeData
                              ) => {
                                data.value = formatAmount(data.value);
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.selectedDefaultValue = data.value;
                                        c.defaultValue = parseFloat(
                                          data.value?.replace(/\./g, '').replace(',', '.') || '0'
                                        );
                                        // Se tem valor padrão, limpa multiplicador
                                        if (c.defaultValue > 0) {
                                          c.multiplier = 0;
                                        }
                                      }
                                      return c;
                                    }
                                  )
                                }));
                              }}
                              component={Input}
                              fluid
                              size="small"
                            />
                          </td>

                          <td style={{ padding: '8px 5px', textAlign: 'center' }}>
                            <Checkbox
                              name={`coverages.${coverage.id}.defaultEnabled`}
                              checked={coverage.defaultEnabled || false}
                              onChange={() => {
                                setCoveragesByPropertyType(prev => ({
                                  ...prev,
                                  [selectedPropertyType]: prev[selectedPropertyType].map(
                                    c => {
                                      if (c.id === coverage.id) {
                                        c.defaultEnabled = !coverage.defaultEnabled;
                                      }
                                      return c;
                                    }
                                  )
                                }));
                              }}
                              disabled={isFireCoverage}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Grid.Column>
            )}

          {categories.find(c => c.active)?.type === 'assistances' &&
            assistances.length > 0 && (
              <Grid.Column
                style={{ marginLeft: '40px' }}
                mobile={16}
                tablet={8}
                computer={8}
              >
                <CoverageTitleRow>
                  <Grid.Column style={{ minWidth: '500px' }}>
                    <span>
                      <h4>ASSISTÊNCIAS DISPONÍVEIS</h4>
                    </span>
                  </Grid.Column>
                </CoverageTitleRow>

                {assistances.map(assistance => {
                  return (
                    <CoverageRow key={assistance.id} isSelected={assistance.selected}>
                      <InsurerCardContent style={{ minWidth: '500px' }}>
                        <Toggle
                          name={assistance.id}
                          id={`assistance-${assistance.id}`}
                          checked={assistance?.selected || false}
                          onChange={() =>
                            setAssistances(
                              assistances.map(a => {
                                if (a.id === assistance.id) {
                                  a.selected = !a.selected;
                                }
                                return a;
                              })
                            )
                          }
                          label={
                            <label>
                              <InsurerCardContentTitle>
                                {assistance.name}
                              </InsurerCardContentTitle>
                            </label>
                          }
                        />
                      </InsurerCardContent>
                    </CoverageRow>
                  );
                })}
              </Grid.Column>
            )}
        </FireInsurersRow>
      </Modal.Content>

      <Modal.Actions>
        <Button
          color="green"
          type="button"
          onClick={(e, data) => {
            try {
              // Consolidar apenas as coberturas HABILITADAS de todos os tipos de imóvel
              const allCoverages: CoverageInterface[] = [];

              PROPERTY_TYPES.forEach(propertyType => {
                const coveragesForType = coveragesByPropertyType[propertyType] || [];

                // Filtrar apenas coberturas habilitadas para reduzir payload
                coveragesForType
                  .filter(coverage => coverage.selected === true)
                  .forEach(coverage => {
                    allCoverages.push({
                      id: coverage.id,
                      enabled: coverage.selected,
                      isRequired: coverage.isRequired,
                      minValue: parseFloat(
                        coverage.selectedMinValue?.replace(/\./g, '').replace(',', '.') ||
                        '0'
                      ),
                      maxValue: parseFloat(
                        coverage.selectedMaxValue?.replace(/\./g, '').replace(',', '.') ||
                        '0'
                      ),
                      multiplier: coverage.multiplier,
                      propertyType: propertyType,
                      name: coverage.name,
                      code: coverage.code,
                      insurerId: coverage.insurerId,
                      channelId: coverage.channelId,
                      maxLmi: coverage.maxLmi,
                      minLmi: coverage.minLmi,
                      value: coverage.value,
                      isDefault: coverage.isDefault,
                      maxMultiplier: coverage.maxMultiplier,
                      selectedMinValue: coverage.selectedMinValue,
                      selectedMaxValue: coverage.selectedMaxValue,
                      label: coverage.label,
                      // Novos campos de default
                      defaultValue: coverage.defaultValue || 0,
                      defaultEnabled: coverage.defaultEnabled ?? false
                    } as any);
                  });
              });

              // Adicionar apenas assistências HABILITADAS (sem propertyType)
              assistances
                .filter(assistance => assistance.selected === true)
                .forEach(assistance => {
                  allCoverages.push({
                    id: assistance.id,
                    enabled: assistance.selected,
                    name: assistance.name,
                    code: assistance.code,
                    insurerId: assistance.insurerId,
                    channelId: assistance.channelId,
                    maxLmi: assistance.maxLmi,
                    minLmi: assistance.minLmi,
                    value: assistance.value,
                    isDefault: assistance.isDefault,
                    maxMultiplier: assistance.maxMultiplier,
                    isRequired: false,
                    minValue: 0,
                    maxValue: 0,
                    multiplier: 0,
                    selectedMinValue: '0',
                    selectedMaxValue: '0'
                  } as any);
                });

              // Debug: Verificar quantas coberturas têm propertyType definido
              const coveragesWithPropertyType = allCoverages.filter(c => c.propertyType);
              const coveragesWithoutPropertyType = allCoverages.filter(
                c => !c.propertyType
              );

              console.log(`📊 Salvando ${allCoverages.length} coberturas:`);
              console.log(`  ✅ Com propertyType: ${coveragesWithPropertyType.length}`);
              console.log(
                `  ❌ Sem propertyType: ${coveragesWithoutPropertyType.length}`
              );

              if (coveragesWithoutPropertyType.length > 0) {
                console.warn(
                  '⚠️ Coberturas sem propertyType:',
                  coveragesWithoutPropertyType.map(c => c.name)
                );
              }

              // Salvar no Formik
              props.setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.coverages',
                allCoverages
              );
              props.setFieldValue(
                'insurerSpecificData.yelum.fireInsurance.assistances',
                assistances
              );

              // Fechar modal
              setOpen(false);
            } catch (error) {
              console.error('Erro ao salvar configurações de coberturas:', error);
              alert('Erro ao salvar configurações. Por favor, tente novamente.');
            }
          }}
        >
          <Icon name="save" />
          Salvar Configurações
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CoveragesConfigModal;
